import _extends from "@babel/runtime/helpers/extends";
import { useCallback, useState, useMemo } from "react";
import { Button, Modal, Form, Spin, Input, Checkbox, Radio, message, Divider } from "antd";
import { request, _i, __ } from "../../utils";
import { HeartTwoTone } from "@ant-design/icons";
import { locationRestPluginFeedbackPost } from "../../wp-api";
const FEEDBACK_FORM_LAYOUT = {
  labelCol: {
    span: 24
  },
  wrapperCol: {
    span: 24
  }
};
const FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM = {
  marginBottom: 8
};

const FeedbackModal = ({
  initialValues = {},
  plugin,
  name,
  hasAtLeastOneActiveLicense,
  isPluginActiveForNetwork,
  privacyPolicy,
  privacyProvider,
  onClose,
  onDeactivate
}) => {
  const reasons = useMemo(() => ({
    "upgrade-to-pro": __("Upgrade to PRO Version"),
    "not-working": __("Plugin does not work"),
    "missing-features": __("Not the features I want"),
    incompatible: __("Incompatible with themes/plugins"),
    "missing-doc": __("Lack of documentation"),
    "found-better-plugin": __("Found a better plugin"),
    temp: __("Temporary deactivation"),
    other: __("Other")
  }), []);
  const [form] = Form.useForm();
  const formName = "license-form-".concat(plugin);
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  const handleClose = useCallback(() => {
    if (window.confirm(__("Are you sure you want to leave the feedback form?"))) {
      setVisible(false);
    }
  }, []);
  const handleFinish = useCallback(async ({
    skip = false,
    reason = "other",
    note = "",
    email = "",
    name = "",
    deactivateLicense = false
  }) => {
    try {
      setBusy(true);
      await request({
        location: locationRestPluginFeedbackPost,
        params: {
          slug: plugin
        },
        request: {
          skip,
          reason,
          note,
          email,
          name: email ? name : "",
          // Only send the name when an email got entered, too
          deactivateLicense
        }
      });
      onDeactivate === null || onDeactivate === void 0 ? void 0 : onDeactivate();
    } catch (e) {
      var _responseJSON$data, _responseJSON$data$bo, _responseJSON$data$bo2;

      const {
        responseJSON
      } = e;
      const code = responseJSON === null || responseJSON === void 0 ? void 0 : (_responseJSON$data = responseJSON.data) === null || _responseJSON$data === void 0 ? void 0 : (_responseJSON$data$bo = _responseJSON$data.body) === null || _responseJSON$data$bo === void 0 ? void 0 : (_responseJSON$data$bo2 = _responseJSON$data$bo[0]) === null || _responseJSON$data$bo2 === void 0 ? void 0 : _responseJSON$data$bo2.code; // Ignore some errors

      if (["DeactivationFeedbackAlreadyGiven", "DeactivationFeedbackMightBeSpam"].indexOf(code) > -1 || !code) {
        onDeactivate === null || onDeactivate === void 0 ? void 0 : onDeactivate();
        return;
      } else {
        var _responseJSON$data2, _responseJSON$data2$b, _responseJSON$data2$b2;

        message.error(responseJSON === null || responseJSON === void 0 ? void 0 : (_responseJSON$data2 = responseJSON.data) === null || _responseJSON$data2 === void 0 ? void 0 : (_responseJSON$data2$b = _responseJSON$data2.body) === null || _responseJSON$data2$b === void 0 ? void 0 : (_responseJSON$data2$b2 = _responseJSON$data2$b[0]) === null || _responseJSON$data2$b2 === void 0 ? void 0 : _responseJSON$data2$b2.message);
      }
    } finally {
      setBusy(false);
    }
  }, [form, plugin]);
  const handleSkip = useCallback(() => {
    const deactivateLicense = form.getFieldValue("deactivateLicense");

    if (deactivateLicense) {
      handleFinish({
        skip: true,
        deactivateLicense
      });
    } else {
      onDeactivate === null || onDeactivate === void 0 ? void 0 : onDeactivate();
    }
  }, [handleFinish]);
  return /*#__PURE__*/React.createElement(Modal, {
    afterClose: onClose,
    onCancel: handleClose,
    visible: visible,
    footer: [/*#__PURE__*/React.createElement(Button, {
      key: "skip",
      type: "default",
      onClick: handleSkip,
      className: "alignleft",
      disabled: busy
    }, /*#__PURE__*/React.createElement("b", null, __("Skip & Deactivate"))), /*#__PURE__*/React.createElement(Button, {
      key: "submit",
      type: "primary",
      htmlType: "submit",
      form: formName,
      disabled: busy
    }, __("Deactivate"))],
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeartTwoTone, {
      twoToneColor: "#eb2f96"
    }), " ", __("Too bad you are leaving"))
  }, /*#__PURE__*/React.createElement(Spin, {
    spinning: busy
  }, /*#__PURE__*/React.createElement(Form, _extends({
    name: formName,
    id: formName,
    form: form
  }, FEEDBACK_FORM_LAYOUT, {
    onFinish: handleFinish,
    initialValues: initialValues,
    layout: "vertical"
  }), /*#__PURE__*/React.createElement(Form.Item, {
    name: "reason",
    label: /*#__PURE__*/React.createElement(React.Fragment, null, __("Please give us feedback why you deactivate %s.", name)),
    style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
    required: true,
    rules: [{
      required: true,
      message: __("Please provide a reason!")
    }]
  }, /*#__PURE__*/React.createElement(Radio.Group, null, Object.keys(reasons).map(choice => /*#__PURE__*/React.createElement(Radio, {
    key: choice,
    value: choice,
    style: {
      width: "calc(50% - 8px)",
      float: "left"
    }
  }, reasons[choice])))), /*#__PURE__*/React.createElement(Form.Item, {
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.reason !== nextValues.reason
  }, ({
    getFieldValue
  }) => !!getFieldValue("reason") && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Form.Item, {
    label: __("What could we do better?"),
    name: "note",
    style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM
  }, /*#__PURE__*/React.createElement(Input.TextArea, {
    autoSize: {
      minRows: 3
    }
  })), /*#__PURE__*/React.createElement(Form.Item, {
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.answerTerms !== nextValues.answerTerms
  }, ({
    getFieldValue
  }) => !!getFieldValue("reason") && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Form.Item, {
    name: "email",
    label: __("Email for answer/solution"),
    style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
    rules: [{
      type: "email",
      required: getFieldValue("answerTerms"),
      message: __("Please provide a valid e-mail address!")
    }]
  }, /*#__PURE__*/React.createElement(Input, null)), /*#__PURE__*/React.createElement(Form.Item, {
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.email !== nextValues.email
  }, ({
    getFieldValue
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, !!getFieldValue("email") && /*#__PURE__*/React.createElement(Form.Item, {
    name: "name",
    label: __("Name"),
    required: true,
    style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
    rules: [{
      required: true,
      message: __("Please provide a name!")
    }]
  }, /*#__PURE__*/React.createElement(Input, null)), /*#__PURE__*/React.createElement(Form.Item, {
    name: "answerTerms",
    valuePropName: "checked",
    required: true,
    rules: [{
      type: "boolean",
      required: !!getFieldValue("email"),
      transform: value => value || undefined,
      message: __("Please confirm that you have checked the privacy policy.")
    }],
    style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM
  }, /*#__PURE__*/React.createElement(Checkbox, {
    style: {
      zoom: 0.8
    }
  }, _i(__("I would like to receive a response to my request. For this purpose, I agree to the data processing of my feedback and my e-mail address. I have read and acknowledge the %s {{a}}Privacy Policy{{/a}}.", privacyProvider), {
    a: /*#__PURE__*/React.createElement("a", {
      href: privacyPolicy,
      target: "_blank",
      rel: "noreferrer"
    })
  }))))))))), /*#__PURE__*/React.createElement(Form.Item, {
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.note !== nextValues.note || prevValues.answerTerms !== nextValues.answerTerms
  }, ({
    getFieldValue
  }) => {
    const answerTerms = getFieldValue("answerTerms") || false;
    const note = getFieldValue("note") || "";

    if (answerTerms) {
      return null;
    }

    return note.split(" ").length >= 5 ? /*#__PURE__*/React.createElement("div", {
      className: "notice notice-info inline below-h2 notice-alt",
      style: {
        margin: 0
      }
    }, /*#__PURE__*/React.createElement("p", null, __("Allow us to reply to you by email and we will get back to you as soon as possible!"))) : /*#__PURE__*/React.createElement("p", {
      className: "description",
      style: {
        marginTop: 5
      }
    }, _i(__("Are there any problems with the setup or use of the plugin? Maybe we can help you in the support. {{a}}Contact support{{/a}}."), {
      a: /*#__PURE__*/React.createElement("a", {
        href: __("https://devowl.io/support/"),
        target: "_blank",
        rel: "noreferrer"
      })
    }));
  }), hasAtLeastOneActiveLicense && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
    style: {
      margin: "12px 0"
    }
  }), /*#__PURE__*/React.createElement(Form.Item, {
    style: {
      marginBottom: 0
    },
    name: "deactivateLicense",
    valuePropName: "checked",
    label: __("Do you want to deactivate your active license so that you can use it again on another site?")
  }, /*#__PURE__*/React.createElement(Checkbox, null, isPluginActiveForNetwork ? __("Yes, deactivate all active licenses for all sites within this multisite") : __("Yes, deactivate license")))))));
};

export { FeedbackModal };